import classnames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'
import { Button } from '../Button/Button'

import styled from '@emotion/styled'

import tokens from '../../design/tokens'

import './SearchForm.scss'

export const SEARCH_CLASSNAME_GROUP = 'b-search-form__group'
export const SEARCH_CLASSNAME_ITEM = 'b-search-form__item'
export const SEARCH_CLASSNAME_LABEL = 'b-search-form__label'

const StyledLabel = styled.label`
  font-weight: ${tokens.fontWeight.bold};
  margin-right: ${tokens.grid.gutterN / 2}px;
  color: ${tokens.color.text.default};
`

export const FormLabel = ({
  children,
  className,
  'data-mu-test': dataAttributeTest,
}) => (
  <StyledLabel className={className} data-mu-test={dataAttributeTest}>
    {children}
  </StyledLabel>
)

FormLabel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  'data-mu-test': PropTypes.string,
}

export const Group = ({ of, children, wide, className }) => (
  <div
    className={classnames(
      SEARCH_CLASSNAME_GROUP,
      of && `${SEARCH_CLASSNAME_GROUP}--of-${of}`,
      wide && 'b-search-form__group--wide',
    )}
  >
    {children}
  </div>
)

Group.propTypes = {
  /**
   * size modifier value. e.g. a value of `5` renders in `--of-5` class modifier
   */
  of: PropTypes.number,
  children: PropTypes.any,
  wide: PropTypes.bool,
  className: PropTypes.string,
}

export const Item = ({ of, children, className }) => (
  <div
    className={classnames(
      SEARCH_CLASSNAME_ITEM,
      of && `${SEARCH_CLASSNAME_ITEM}--of-${of}`,
      className,
    )}
  >
    {children}
  </div>
)

Item.propTypes = {
  /**
   * size modifier value. e.g. a value of `5` renders in `--of-5` class modifier
   */
  of: PropTypes.number,
  children: PropTypes.any,
  className: PropTypes.string,
}

export const SearchForm = ({
  className,
  advanced,
  expanded,
  labelBtnSearch,
  labelBtnReset,
  onClickToggle,
  onClickReset,
  onClickSubmit,
  onSubmit,
  AdvancedSearchComponent,
  advancedSearchComponentProps,
  submitClassName,
  resetClassName,
  children,
  isSubmitDisabled,
}) => {
  const handleClickToggle = (e) => {
    onClickToggle(e)
  }

  const handleClickReset = (e) => {
    e.preventDefault()
    onClickReset(e)
  }

  const handleClickSubmit = (e) => {
    typeof onClickSubmit === 'function' && onClickSubmit(e)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    typeof onSubmit === 'function' && onSubmit(e)
  }

  return (
    <form
      className={classnames(className, 'b-search-form', {
        'b-search-form--expanded': expanded,
      })}
      onSubmit={handleSubmit}
    >
      <div className='b-search-form__main'>
        <div className='b-search-form__group b-search-form__group--controls'>
          <div className='b-search-form__control'>
            <Button
              kind='neutral'
              onClick={handleClickReset}
              className={resetClassName}
              data-mu-test='search-form__button--reset'
              type='reset'
            >
              {labelBtnReset}
            </Button>
          </div>

          <div
            className={classnames('b-search-form__control', {
              'btn-group-select': advanced,
            })}
          >
            <Button
              kind='secondary'
              onClick={handleClickSubmit}
              className={classnames('btn-text btn-medium', submitClassName)}
              data-mu-test='search-form__button--search'
              type='submit'
              disabled={isSubmitDisabled}
            >
              {labelBtnSearch}
            </Button>
            {advanced && (
              <a className='btn-icon btn-medium' onClick={handleClickToggle}>
                <span
                  className={classnames('btn-group-select_advanced-search', {
                    'is-open': expanded,
                    'is-close': !expanded,
                  })}
                  data-mu-test='search-form__button--expand'
                />
              </a>
            )}
          </div>
        </div>
        {children}
      </div>

      {advanced && AdvancedSearchComponent && (
        <div className='b-search-form__advanced'>
          <AdvancedSearchComponent {...advancedSearchComponentProps} />
        </div>
      )}
    </form>
  )
}

SearchForm.defaultProps = {
  advanced: true,
  advancedExpanded: false,
  isSubmitDisabled: false,
}

SearchForm.propTypes = {
  /**
   * enable collapsible section
   */
  advanced: PropTypes.bool,
  /**
   * element to display inside the collapsible section
   */
  AdvancedSearchComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * props of <AdvancedSearchComponent />
   */
  advancedSearchComponentProps: PropTypes.object,
  /**
   * wether the advanced section in expanded or not
   */
  expanded: PropTypes.bool,
  /**
   * label for the reset button
   */
  labelBtnReset: PropTypes.string,
  /**
   * label for the search button
   */
  labelBtnSearch: PropTypes.string,
  /**
   * elements of the primary (not collapsible) section of the search form
   */
  children: PropTypes.node,
  /**
   * additional classnames
   */
  className: PropTypes.string,
  /**
   * callback for click event on reset button
   */
  onClickReset: PropTypes.func,
  /**
   * callback for click event on search button
   */
  onClickSubmit: PropTypes.func,
  /**
   * callback for click event on toggle button (only id `advanced` is `true`)
   */
  onClickToggle: PropTypes.func,
  isSubmitDisabled: PropTypes.bool,
}
